import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { COMPANY_URL, ORDERS_URL, RECALL_RESPONSE } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { KeyValueObj } from "@/common/types";
import { IAddedBatch, IProductBatches } from "@/common/types/others";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import Stepper, { IStep } from "@/common/components/stepper";
import DeliveryInfo from "./delivery-info";
import Preview from "./preview";
import AutoScanner from "./scanner";
import { NewDeliveryWrapper } from "../new-delivery/style";
import useQuery from "@/hooks/useQuery";
import { DELIVERY_PACKAGE_TYPES, toDeliveryCategory } from "../data";
import { todayAsDateString } from "@/common/utils/isContain";

interface IBatch {
  batch_number: string;
  quantity: number;
  inventory_id: string;
  brand_name: string;
  strength: string;
  strength_unit: string;
}

const steps: IStep[] = [
  { id: 1, label: "Batch Selection" },
  { id: 2, label: "Delivery information" },
  { id: 3, label: "Preview" },
];

const EMPTY_FORM = {
  batch_details: {} as IProductBatches,
  logistics_company: [],
  expected_delivery_date: "",
  recipent_phone_number: "",
  sender_phone_number: "",
  pickup_option: "",
  pickup_address: null,
  vehicle_needed: "",
  category: "",
  min_temperature: null,
  max_temperature: null,
  length: null,
  width: null,
  height: null,
  package_type: "",
};

function formatDateRange(deliveryDate: string): string {
  const format = (d: Date) =>
    [d.getDate(), d.getMonth() + 1, d.getFullYear()]
      .map((n) => String(n).padStart(2, "0"))
      .join("/");
  return `${format(new Date())}-${format(new Date(deliveryDate))}`;
}

function transformToProducts(data?: Record<string, IBatch[]> | null) {
  if (!data || typeof data !== "object") return [];
  return Object.entries(data).map(([atc_code_id, batching]) => ({
    atc_code_id,
    batching,
  }));
}

function transformDeliveryData(input: KeyValueObj) {
  return [
    {
      category: input.category,
      length: input.length || 0,
      width: input.width || 0,
      height: input.height || 0,
      package_type: input.package_type || "",
      min_temperature: input.min_temperature || "",
      max_temperature: input.max_temperature || "",
    },
  ];
}

function buildRecipientDetails(
  formData: KeyValueObj,
  productSource: KeyValueObj,
  manufacturerAddress: KeyValueObj,
  source: ISource,
) {
  const data =
    source === ISource.ORDER
      ? {
          id: productSource?.owner?.id,
          custom_address: {
            state: productSource?.address?.state,
            lga: productSource?.address?.lga,
            city: productSource?.address?.city,
            street: productSource?.address?.street,
            building_number: productSource?.address?.building_number,
          },
          secondary_phone_number: formData?.recipent_phone_number,
        }
      : {
          id: productSource?.recalled_product?.product?.owner_id,
          custom_address: {
            state: manufacturerAddress?.state,
            lga: manufacturerAddress?.lga,
            city: manufacturerAddress?.city,
            street: manufacturerAddress?.street,
            building_number: manufacturerAddress?.building_number,
          },
          secondary_phone_number:
            formData?.recipent_phone_number ||
            productSource?.owner?.phone_number,
        };
  return data;
}

function buildSenderDetails(formData: KeyValueObj) {
  return {
    secondary_phone_number: formData?.sender_phone_number,
    address_id: formData?.pickup_address?.id,
    custom_address: {
      state: formData?.pickup_address?.state,
      lga: formData?.pickup_address?.lga,
      city: formData?.pickup_address?.city,
      street: formData?.custom_address?.street,
      building_number: formData?.pickup_address?.building_number,
    },
  };
}

export enum ISource {
  RECALL = "recall",
  ORDER = "order",
}

// MED-723 — the delivery fields the originating order (or recall) can answer.
//
// Only what the source genuinely carries. `products` stores the storage
// requirement, the packaging levels and, since MED-723, the shipping dimensions
// and temperature band; the order additionally carries the date the buyer
// expects delivery. Everything else on the form — logistics company, preferred
// vehicle, the secondary phone numbers, pickup choice — is a decision about
// THIS delivery rather than a fact about the order, and is deliberately left
// empty for the user to make.
//
// A key is omitted entirely when the source has no answer, so the caller can
// merge this without blanking a field the user has already filled in.
function buildPrefillFromSource(
  productDetails: KeyValueObj | undefined,
  sourceData: KeyValueObj | undefined,
  source: ISource,
): KeyValueObj {
  const prefill: KeyValueObj = {};
  if (!productDetails) return prefill;

  const category = toDeliveryCategory(productDetails?.storage_requirement);
  if (category) prefill.category = category;

  // The outermost packaging the product defines is what a delivery ships in.
  // Guarded against the enum the logistics service accepts: a product packaged
  // as something it has no value for (e.g. "strip") leaves the field empty to
  // be chosen rather than prefilling a value that is refused on submit.
  const packaging =
    productDetails?.tertiary_packaging ||
    productDetails?.secondary_packaging ||
    productDetails?.primary_packaging;
  if (packaging && DELIVERY_PACKAGE_TYPES.includes(packaging)) {
    prefill.package_type = packaging;
  }

  // MED-723 columns on `products`. Null on every product created before them,
  // hence the per-field presence check rather than a blanket copy — writing
  // `null` into the form would render as an empty box either way, but would
  // also overwrite a value on a re-run.
  (["length", "width", "height", "min_temperature", "max_temperature"] as const).forEach(
    (field) => {
      const value = productDetails?.[field];
      if (value !== null && value !== undefined && value !== "") {
        prefill[field] = value;
      }
    },
  );

  // The order's own expected delivery date, and only when it is still reachable.
  // An order placed weeks ago carries a date now in the past; prefilling that
  // would open the form on a value its own validation rejects. A recall has no
  // equivalent date.
  if (source === ISource.ORDER) {
    const orderDate = sourceData?.expected_delivery_date;
    if (orderDate && String(orderDate).slice(0, 10) >= todayAsDateString()) {
      prefill.expected_delivery_date = String(orderDate).slice(0, 10);
    }
  }

  return prefill;
}

const FROM_CONFIG: Record<
  NonNullable<ISource>,
  {
    link: string;
    extractProduct: (data: any) => any;
    dashboardLink: string;
    label: string;
    submissionLink: string;
  }
> = {
  order: {
    link: ORDERS_URL,
    extractProduct: (data) => data?.order_products?.[0],
    dashboardLink: "/dashboard/requisition",
    label: "Orders",
    submissionLink: ORDERS_URL + `/shipped/`,
  },
  recall: {
    link: RECALL_RESPONSE,
    extractProduct: (data) => data?.recalled_product?.product,
    dashboardLink: "/dashboard/recall",
    label: "Recall",
    submissionLink: RECALL_RESPONSE,
  },
};

const AutoDelivery = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") as ISource;
  const [loading, setLoading] = useState<boolean>(false);
  const sourceConfig = FROM_CONFIG[from];
  if (!from || !sourceConfig) {
    navigate(-1);
    return null;
  }

  const queryUrl = useMemo(() => {
    if (!from || !FROM_CONFIG[from]) return null;
    return `${FROM_CONFIG[from].link}/${id}`;
  }, [from, id]);

  const { data: deliverySourceData } = useQuery(queryUrl as string);
  const product =
    from === ISource.ORDER
      ? deliverySourceData?.order_products?.[0]
      : deliverySourceData?.recalled_product?.product;

  const apiCall = useApiCallHandling();
  const { data: recipientData } = useQuery(
    COMPANY_URL + `/${product?.owner_id}`,
    from === ISource.RECALL,
  );

  const manufacturerAddress = recipientData?.addresses?.[0];

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([false, false, false]);

  const [formData, setFormData] = useState<KeyValueObj>(EMPTY_FORM);
  const [productBatches, setProductBatches] = useState<IProductBatches>({});

  // MED-723: the source's answers are seeded ONCE, when they arrive.
  //
  // They used to be written by `markCurrentStepComplete`, which runs on every
  // step transition — so stepping back into Delivery Information and forward
  // again re-applied the order's values over whatever the user had changed
  // them to. A prepopulated field the user edits has to keep the edit; that is
  // the value the delivery carries.
  //
  // The ref, not the step index, is what makes it once: `deliverySourceData`
  // can resolve after the first render, and the query refetching must not
  // re-seed either.
  const hasSeededPrefill = useRef(false);

  useEffect(() => {
    if (hasSeededPrefill.current) return;
    const productData =
      from === ISource.ORDER ? product?.product_details : product;
    if (!productData) return;

    const prefill = buildPrefillFromSource(productData, deliverySourceData, from);
    hasSeededPrefill.current = true;
    if (Object.keys(prefill).length === 0) return;

    // Merged UNDER `prev`, not over it: anything already in the form — the user
    // got to the field before the order data landed — wins over the prefill.
    setFormData((prev) => {
      const merged = { ...prev };
      Object.entries(prefill).forEach(([key, value]) => {
        const current = merged[key];
        if (current === undefined || current === null || current === "") {
          merged[key] = value;
        }
      });
      return merged;
    });
  }, [product, deliverySourceData, from]);

  const markCurrentStepComplete = () => {
    setCompletedSteps((prev) => {
      const updated = [...prev];
      updated[currentStep] = true;
      return updated;
    });
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goBack = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const handleStepChange = (index: number) => {
    if (index <= currentStep || completedSteps[index - 1]) {
      setCurrentStep(index);
    }
  };

  const handleProductSelected = (
    batches: IAddedBatch[],
    orderProduct: KeyValueObj,
    serials: string[],
  ) => {
    const updated = {
      ...productBatches,
      [orderProduct?.atc_code_id ?? orderProduct?.id]: batches,
    };
    setProductBatches(updated);
    setFormData((prev) => ({
      ...prev,
      batch_details: updated,
      scanned_serials: serials,
    }));

    markCurrentStepComplete();
  };

  const handleSubmit = () => {
    const payload = {
      estimated_delivery_date_range: formatDateRange(
        formData?.expected_delivery_date,
      ),
      products: transformToProducts(formData?.batch_details),
      logistics_company: formData?.logistics_company?.map((d: any) => d.id),
      preferred_vehicle_type: formData?.vehicle_needed,
      pickup_option: formData?.pickup_option,
      expected_delivery_date: formData?.expected_delivery_date,
      sender_details: buildSenderDetails(formData),
      recipient_details: buildRecipientDetails(
        formData,
        deliverySourceData,
        manufacturerAddress,
        from,
      ),
      delivery_items_details: transformDeliveryData(formData),
      scanned_serials: formData?.scanned_serials,
    };
    setLoading(true);
    apiCall(
      from === ISource.ORDER
        ? FROM_CONFIG[from]?.submissionLink + id
        : FROM_CONFIG[from]?.submissionLink + `/${id}/create-delivery-request`,
      "POST",
      payload,
      () => navigate(FROM_CONFIG[from]?.dashboardLink),
      console.log,
      () => setLoading(false),
    );
  };

  return (
    <NewDeliveryWrapper>
      <PageTitleNavs
        title="Auto Delivery"
        backLink={sourceConfig.dashboardLink}
        bcrumbs={[
          {
            title: sourceConfig?.label,
            link: sourceConfig?.dashboardLink,
          },
        ]}
      />

      <p className="text-sm text-gray-500 mt-1 mb-6">
        Set the batches, the corresponding quantity and the delivery details
      </p>
      <div className="main-wrapper">
        <Stepper
          steps={steps}
          currentStep={currentStep}
          onStepChange={handleStepChange}
          className="w-full"
        />

        <div className="mt-8 w-full">
          {currentStep === 0 && (
            <AutoScanner
              source={from as ISource}
              data={product}
              handleProductSelected={handleProductSelected}
            />
          )}

          {currentStep === 1 && (
            <DeliveryInfo
              onBack={goBack}
              formData={formData}
              setFormData={setFormData}
              onContinue={markCurrentStepComplete}
            />
          )}

          {currentStep === 2 && (
            <Preview
              productBatches={productBatches}
              formData={formData}
              setFormData={handleSubmit}
              onBack={goBack}
              loading={loading}
            />
          )}
        </div>
      </div>
    </NewDeliveryWrapper>
  );
};

export default AutoDelivery;
